import React, { useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { Link } from 'react-router-dom';
import { useCartStore } from '@/store/cartStore';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { getAppSettings } from '@/api/settings';
import { useTranslation } from 'react-i18next';
import { handleSupabaseError } from '@/lib/supabaseClient';
import { supabase } from '@/lib/supabase';

export function Cart() {
  // Get cart data directly from store
  const { items, removeItem, updateQuantity, total } = useCartStore();
  
  // State management
  const [settings, setSettings] = useState({ minOrderAmount: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [stockLevels, setStockLevels] = useState<Record<string, number>>({});
  const [updating, setUpdating] = useState<string | null>(null);
  const { t } = useTranslation();

  // Load settings and stock levels on component mount
  useEffect(() => {
    let isMounted = true;
    
    async function loadData() {
      try {
        // Load settings
        const data = await getAppSettings();
        if (isMounted) {
          setSettings(data);
        }

        // Load stock levels for all items in cart
        if (items.length > 0) {
          const { data: stockData, error: stockError } = await supabase
            .from('products')
            .select('id, stock_level')
            .in('id', items.map(item => item.id));

          if (stockError) throw stockError;

          if (isMounted && stockData) {
            const levels = stockData.reduce((acc, product) => ({
              ...acc,
              [product.id]: product.stock_level
            }), {});
            setStockLevels(levels);
          }
        }
      } catch (error) {
        handleSupabaseError(error, 'Cart:loadData');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [items]);

  // Handle quantity changes with error catching
  async function handleQuantityChange(id: string, newQuantity: number) {
    try {
      if (newQuantity >= 1) {
        setUpdating(id);
        await updateQuantity(id, newQuantity);
        
        // Refresh stock levels after update
        const { data: stockData, error: stockError } = await supabase
          .from('products')
          .select('stock_level')
          .eq('id', id)
          .single();

        if (stockError) throw stockError;

        if (stockData) {
          setStockLevels(prev => ({
            ...prev,
            [id]: stockData.stock_level
          }));
        }
      }
    } catch (error) {
      logger.error('Error updating quantity:', error);
    } finally {
      setUpdating(null);
    }
  }

  // Calculate cart total using the store's total function
  let cartTotal = 0;
  try {
    cartTotal = total();
  } catch (error) {
    logger.error('Error calculating total:', error);
  }
  
  // Determine if order meets minimum amount (only if minimum is set)
  const isOrderBelowMinimum = settings.minOrderAmount > 0 && cartTotal < settings.minOrderAmount;

  // Show loading indicator
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8">Kundvagn</h1>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
        </div>
      </div>
    );
  }

  // Show empty cart message
  if (!items || items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8" data-testid="cart-heading">Kundvagn</h1>
        <div className="text-center" data-testid="empty-cart-message">
          <p className="text-gray-500 mb-4">Din kundvagn är tom</p>
          <Link
            to="/products"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            data-testid="continue-shopping-button"
          >
            Fortsätt handla
          </Link>
        </div>
      </div>
    );
  }

  // Main cart view
  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-8" data-testid="cart-heading">Kundvagn</h1>
      <div>
        <div className="bg-white shadow-sm rounded-md overflow-hidden mb-8">
          <ul className="divide-y divide-gray-200" data-testid="cart-items">
            {items.map((item) => (
              <li key={item.id} className="px-4 py-4 sm:px-6" data-testid="cart-item">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-md"></div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900" data-testid="item-name">{item.name}</div>
                      <div className="text-sm text-gray-500" data-testid="item-price">
                        {typeof item.price === 'number' 
                          ? item.price.toLocaleString('sv-SE') 
                          : '0'} Kr / st
                      </div>
                      {stockLevels[item.id] !== undefined && item.quantity >= stockLevels[item.id] && (
                        <div className="text-sm text-red-600 mt-1" data-testid="stock-warning">
                          Max antal tillgängligt: {stockLevels[item.id]} st
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex items-center border border-gray-300 rounded-md" data-testid="quantity-controls">
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        className="px-2 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        disabled={updating === item.id}
                        data-testid="decrease-quantity"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="px-2 py-1 font-medium text-gray-700" data-testid="item-quantity">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        className="px-2 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        disabled={updating === item.id || (stockLevels[item.id] !== undefined && item.quantity >= stockLevels[item.id])}
                        data-testid="increase-quantity"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="ml-4 text-sm font-medium text-gray-900 w-20 text-right" data-testid="item-total">
                      {(typeof item.price === 'number' ? item.price * item.quantity : 0).toLocaleString('sv-SE')} Kr
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="ml-4 text-red-500 hover:text-red-700"
                      disabled={updating === item.id}
                      data-testid="remove-item"
                    >
                      <Trash2 className="h-5 w-5" />
                      <span className="sr-only">Ta bort</span>
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-lg font-medium text-gray-900" data-testid="cart-total">
            Totalt: {cartTotal.toLocaleString('sv-SE')} Kr
          </p>
          
          <div className="flex flex-col items-end">
            {isOrderBelowMinimum && (
              <div className="mb-2 text-sm text-red-600" data-testid="minimum-order-warning">
                <p>Minsta beställning på {settings.minOrderAmount.toLocaleString('sv-SE')} kr krävs</p>
                <p className="mt-1">
                  {t('cart.orderMore', 'Lägg till fler produkter för att nå minimibeloppet')}
                </p>
              </div>
            )}
            
            <Link
              to="/checkout"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                isOrderBelowMinimum
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              onClick={(e) => {
                if (isOrderBelowMinimum) {
                  e.preventDefault();
                }
              }}
              aria-disabled={isOrderBelowMinimum}
              data-testid="checkout-button"
            >
              Gå till kassan
            </Link>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-red-600 font-medium" data-testid="delivery-notice">
            För att få leverans nästkommande torsdag måste beställningen ske senast onsdagen innan kl 12:00.
          </p>
        </div>
      </div>
    </div>
  );
}